/* eslint-disable camelcase */
/* eslint-disable react/jsx-no-bind */
import PropTypes from 'prop-types';
import React, { useState, useEffect, memo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Card,
  Stack,
  Divider,
  Button,
  Alert,
  useMediaQuery,
  useTheme,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel, Paper,
  IconButton
} from '@mui/material';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  useForm,
  Controller,
  FormProvider,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import axiosInstance, { getErrorMessage } from 'utils/axios';
import { RHFTextField } from 'components/hook-form';
import { LoadingButton } from '@mui/lab';
import CRUD from 'utils/Crud';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Icon } from '@iconify/react';


// Helper for default scheduled_at input (yyyy-MM-ddTHH:mm)
function getDefaultScheduledAt() {
  const date = new Date();
  date.setHours(date.getHours() + 1);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}

const categoryOptions = [
  { label: 'General', value: 'general' },
  { label: 'Crypto', value: 'does_crypto' },
  { label: 'Giftcard', value: 'does_giftcard' },
];

const MAX_TOPIC_LENGTH = 50;
const MAX_MESSAGE_LENGTH = 200;

// Dynamic schema generator based on filter toggles
const makeSchema = (filterSignupDate, filterLastSeenDate) =>
  Yup.object().shape({
    topic: Yup.string()
      .required('Topic is required')
      .max(MAX_TOPIC_LENGTH, `Topic must be at most ${MAX_TOPIC_LENGTH} characters`),
    message: Yup.string()
      .required('Message is required')
      .max(MAX_MESSAGE_LENGTH, `Message must be at most ${MAX_MESSAGE_LENGTH} characters`),
    category: Yup.string()
      .oneOf(categoryOptions.map((c) => c.value))
      .required('Category is required'),
    signupDateFrom: Yup.date()
      .nullable()
      .when('filterSignupDate', {
        is: true,
        then: Yup.date()
          .required('Signup Date From is required when filtered')
          .typeError('Invalid date')
          .nullable(false),
        otherwise: Yup.date().nullable(),
      }),
    signupDateTo: Yup.date()
      .nullable()
      .when(['filterSignupDate', 'signupDateFrom'], {
        is: (filterDate, from) => filterDate === true && !!from,
        then: Yup.date()
          .required('Signup Date To is required when filtered')
          .min(Yup.ref('signupDateFrom'), "End date can't be before start date")
          .typeError('Invalid date')
          .nullable(false),
        otherwise: Yup.date().nullable(),
      }),
    lastSeenFrom: Yup.date()
      .nullable()
      .when('filterLastSeenDate', {
        is: true,
        then: Yup.date()
          .required('Last Seen From is required when filtered')
          .typeError('Invalid date')
          .nullable(false),
        otherwise: Yup.date().nullable(),
      }),
    lastSeenTo: Yup.date()
      .nullable()
      .when(['filterLastSeenDate', 'lastSeenFrom'], {
        is: (filterDate, from) => filterDate === true && !!from,
        then: Yup.date()
          .required('Last Seen To is required when filtered')
          .min(Yup.ref('lastSeenFrom'), "End date can't be before start date")
          .typeError('Invalid date')
          .nullable(false),
        otherwise: Yup.date().nullable(),
      }),
    scheduled_at: Yup.date()
      .min(new Date(), 'Scheduled time must be in the future')
      .required('Scheduled datetime is required'),
    password: Yup.string().required('Password is required'),
    filterSignupDate: Yup.boolean(),
    filterLastSeenDate: Yup.boolean(),
  });

CreateCampaignDialog.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  handleComplete: PropTypes.func,
};

// Memoized Form Content to prevent unnecessary re-renders
const FormContent = memo(({
  methods,
  isMobile,
  filterSignupDate,
  filterLastSeenDate,
  previewLoading,
  isSubmitting,
  errors,
  control,
  setFilterSignupDate,
  setFilterLastSeenDate,
  reset,
  handleClose,
}) => (
  <LeftPaneContent>
    {/* Topic */}
    <Box sx={{ mb: 1 }}>
      <Controller
        name="topic"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            label={`Topic (max ${MAX_TOPIC_LENGTH} chars)`}
            placeholder="Enter notification topic"
            fullWidth
            inputProps={{ maxLength: MAX_TOPIC_LENGTH }}
            error={!!error}
            helperText={error ? error.message : `${field.value?.length || 0} / ${MAX_TOPIC_LENGTH}`}
            size="small"
          />
        )}
      />
    </Box>

    {/* Message */}
    <Box sx={{ mb: 1 }}>
      <Controller
        name="message"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            label={`Message (max ${MAX_MESSAGE_LENGTH} chars)`}
            placeholder="Enter notification message"
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            sx={{ whiteSpace: 'pre-line' }}
            inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
            error={!!error}
            helperText={error ? error.message : `${field.value?.length || 0} / ${MAX_MESSAGE_LENGTH}`}
            size="small"
          />
        )}
      />
    </Box>

    {/* Category Filter */}
    <Box sx={{ mb: 2 }}>
      <FormControl fullWidth size="small">
        <Controller
          name="category"
          control={control}
          render={({ field }) => (
            <>
              <InputLabel id="category-label">Category Filter</InputLabel>
              <Select
                labelId="category-label"
                label="Category Filter"
                {...field}
                fullWidth
                MenuProps={{ disablePortal: false }}
                size="small"
              >
                {categoryOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}
        />
      </FormControl>
      {errors.category && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
          {errors.category.message}
        </Typography>
      )}
    </Box>

    {/* Filter Signup Date Switch */}
    <FormControlLabel
      sx={{ mb: 1 }}
      control={
        <Switch
          checked={filterSignupDate}
          onChange={(e) => setFilterSignupDate(e.target.checked)}
          color="primary"
          size="small"
        />
      }
      label="Filter Signup Date"
    />

    {/* Conditionally render Signup Date pickers */}
    {filterSignupDate && (
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={1}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <FormControl sx={{ flex: 1 }} size="small">
          <Controller
            name="signupDateFrom"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Signup Date From"
                  {...field}
                  value={field.value}
                  onChange={(newValue) => field.onChange(newValue)}
                  renderInput={(params) => (
                    <TextField
                      fullWidth
                      {...params}
                      error={!!errors.signupDateFrom}
                      helperText={errors.signupDateFrom?.message}
                      size="small"
                    />
                  )}
                />
              </LocalizationProvider>
            )}
          />
        </FormControl>

        <FormControl sx={{ flex: 1 }} size="small">
          <Controller
            name="signupDateTo"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Signup Date To"
                  {...field}
                  value={field.value}
                  onChange={(newValue) => field.onChange(newValue)}
                  renderInput={(params) => (
                    <TextField
                      fullWidth
                      {...params}
                      error={!!errors.signupDateTo}
                      helperText={errors.signupDateTo?.message}
                      size="small"
                    />
                  )}
                />
              </LocalizationProvider>
            )}
          />
        </FormControl>
      </Stack>
    )}

    {/* Filter Last Seen Date Switch */}
    <FormControlLabel
      sx={{ mb: 1 }}
      control={
        <Switch
          checked={filterLastSeenDate}
          onChange={(e) => setFilterLastSeenDate(e.target.checked)}
          color="primary"
          size="small"
        />
      }
      label="Filter Last Seen Date"
    />

    {/* Conditionally render Last Seen Date pickers */}
    {filterLastSeenDate && (
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={1}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <FormControl sx={{ flex: 1 }} size="small">
          <Controller
            name="lastSeenFrom"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Last Seen From"
                  {...field}
                  value={field.value}
                  onChange={(newValue) => field.onChange(newValue)}
                  renderInput={(params) => (
                    <TextField
                      fullWidth
                      {...params}
                      error={!!errors.lastSeenFrom}
                      helperText={errors.lastSeenFrom?.message}
                      size="small"
                    />
                  )}
                />
              </LocalizationProvider>
            )}
          />
        </FormControl>

        <FormControl sx={{ flex: 1 }} size="small">
          <Controller
            name="lastSeenTo"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Last Seen To"
                  {...field}
                  value={field.value}
                  onChange={(newValue) => field.onChange(newValue)}
                  renderInput={(params) => (
                    <TextField
                      fullWidth
                      {...params}
                      error={!!errors.lastSeenTo}
                      helperText={errors.lastSeenTo?.message}
                      size="small"
                    />
                  )}
                />
              </LocalizationProvider>
            )}
          />
        </FormControl>
      </Stack>
    )}

    {/* Scheduled DateTime */}
    <Box sx={{ mb: 1 }}>
      <RHFTextField
        name="scheduled_at"
        label="Scheduled Dispatch Date & Time"
        type="datetime-local"
        InputLabelProps={{ shrink: true }}
        fullWidth
        inputProps={{
          step: 60,
        }}
        error={!!errors.scheduled_at}
        helperText={errors.scheduled_at?.message}
        size="small"
      />
    </Box>

    {/* Confirm Password */}
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
        Confirm Password
      </Typography>
      <RHFTextField
        name="password"
        label="Password"
        type="password"
        fullWidth
        placeholder="Enter your password"
        sx={{ input: { fontSize: isMobile ? 14 : 16 } }}
        error={!!errors.password}
        helperText={errors.password?.message}
        size="small"
      />
    </Box>

    {/* Actions */}
    <Stack
      direction={isMobile ? 'column' : 'row'}
      spacing={1}
      justifyContent="flex-end"
      sx={{ mt: 'auto' }}
    >
      <Button
        variant="outlined"
        color="inherit"
        onClick={() => {
          reset();
          setFilterSignupDate(false);
          setFilterLastSeenDate(false);
          handleClose();
        }}
        sx={{
          minWidth: 100,
          fontSize: isMobile ? 14 : 16,
          py: 1,
        }}
      >
        Cancel
      </Button>

      <LoadingButton
        type="submit"
        variant="contained"
        size="large"
        loading={previewLoading || isSubmitting}
        sx={{
          minWidth: 130,
          fontSize: isMobile ? 14 : 16,
          py: 1,
        }}
      >
        Preview Campaign
      </LoadingButton>
    </Stack>
  </LeftPaneContent>
));


export default function CreateCampaignDialog({
  open,
  handleClose,
  handleComplete,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Manage filter toggle switches state locally
  const [filterSignupDate, setFilterSignupDate] = useState(false);
  const [filterLastSeenDate, setFilterLastSeenDate] = useState(false);

  // New loading state for final submit button
  const [submitLoading, setSubmitLoading] = useState(false);

  // Set up form methods with dynamic schema
  const methods = useForm({
    resolver: yupResolver(makeSchema(filterSignupDate, filterLastSeenDate)),
    mode: 'onBlur',
    defaultValues: {
      topic: '',
      message: '',
      category: '',
      filterSignupDate: false,
      signupDateFrom: null,
      signupDateTo: null,
      filterLastSeenDate: false,
      lastSeenFrom: null,
      lastSeenTo: null,
      scheduled_at: getDefaultScheduledAt(), // string, not Date object
      password: '',
    },
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting, errors },
    getValues,
    control,
    setError,
    setValue,
    clearErrors,
  } = methods;

  // Synchronize local state with react-hook-form values and clear errors if filter is toggled off
  useEffect(() => {
    setValue('filterSignupDate', filterSignupDate);
    if (!filterSignupDate) {
      setValue('signupDateFrom', null);
      setValue('signupDateTo', null);
      clearErrors(['signupDateFrom', 'signupDateTo']);
    }
  }, [filterSignupDate, setValue, clearErrors]);

  useEffect(() => {
    setValue('filterLastSeenDate', filterLastSeenDate);
    if (!filterLastSeenDate) {
      setValue('lastSeenFrom', null);
      setValue('lastSeenTo', null);
      clearErrors(['lastSeenFrom', 'lastSeenTo']);
    }
  }, [filterLastSeenDate, setValue, clearErrors]);

  const [previewMode, setPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  async function onPreview(values) {
    setPreviewLoading(true);
    try {
      const response = await axiosInstance.post(CRUD.campaign.eligibleUsers, {
        topic: values.topic,
        message: values.message,
        category: values.category,
        signup_date_from:
          filterSignupDate && values.signupDateFrom
            ? new Date(values.signupDateFrom).toISOString()
            : undefined,
        signup_date_to:
          filterSignupDate && values.signupDateTo
            ? new Date(values.signupDateTo).toISOString()
            : undefined,
        last_seen_from:
          filterLastSeenDate && values.lastSeenFrom
            ? new Date(values.lastSeenFrom).toISOString()
            : undefined,
        last_seen_to:
          filterLastSeenDate && values.lastSeenTo
            ? new Date(values.lastSeenTo).toISOString()
            : undefined,
        filter_signup: values.filterSignupDate,
        filter_last_seen: values.filterLastSeenDate,
        scheduled_at: new Date(values.scheduled_at).toISOString(),
      });

      const { affected_users } = response.data;

      if (typeof affected_users !== 'number') { // || !token
        throw new Error('Invalid response from server');
      }

      setPreviewData({
        topic: values.topic,
        message: values.message,
        affected_users,
        // token,
      });
      setPreviewMode(true);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    } finally {
      setPreviewLoading(false);
    }
  }

  async function onSubmitCampaign() {
    if (!previewData) return;
    setSubmitLoading(true);

    const values = getValues();

    try {
      const payload = {
        topic: values.topic,
        message: values.message,
        category: values.category,
        signup_date_from:
          filterSignupDate && values.signupDateFrom
            ? new Date(values.signupDateFrom).toISOString()
            : undefined,
        signup_date_to:
          filterSignupDate && values.signupDateTo
            ? new Date(values.signupDateTo).toISOString()
            : undefined,
        last_seen_from:
          filterLastSeenDate && values.lastSeenFrom
            ? new Date(values.lastSeenFrom).toISOString()
            : undefined,
        last_seen_to:
          filterLastSeenDate && values.lastSeenTo
            ? new Date(values.lastSeenTo).toISOString()
            : undefined,
        scheduled_at: new Date(values.scheduled_at).toISOString(),
        password: values.password,
        // token: previewData.token,
        filter_signup: values.filterSignupDate,
        filter_last_seen: values.filterLastSeenDate,
      };

      const ret = await axiosInstance.post(CRUD.campaign.create, payload);

      enqueueSnackbar(ret.data.message, { variant: 'success' });
      reset();
      setFilterSignupDate(false);
      setFilterLastSeenDate(false);
      setPreviewMode(false);
      setPreviewData(null);
      handleComplete(true);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    } finally {
      setSubmitLoading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        setFilterSignupDate(false);
        setFilterLastSeenDate(false);
        setPreviewMode(false);
        setPreviewData(null);
        handleClose();
      }}
      fullScreen
      aria-labelledby="create-campaign-title"
    >
      <DialogTitle
        sx={{
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',  // This will align title left and button right
          minHeight: 56,
          pr: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Create Notification Campaign
        </Typography>

        <IconButton
          aria-label="close"
          onClick={() => {
            reset();
            setFilterSignupDate(false);
            setFilterLastSeenDate(false);
            setPreviewMode(false);
            setPreviewData(null);
            handleClose();
          }}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Icon icon="ic:round-close" width="24" height="24" />
        </IconButton>
      </DialogTitle>


      <DialogContent
        dividers
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          px: 2,
          py: 1,
          minHeight: '80vh',
        }}
      >
        {/* Left Pane: Inputs */}
        <Card
          elevation={3}
          sx={{
            flex: 1,
            p: isMobile ? 1 : 3,
            mr: isMobile ? 0 : 2,
            mb: isMobile ? 2 : 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(onPreview)}
              noValidate
              style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
            >
              {!previewMode && (
                <FormContent
                  methods={methods}
                  isMobile={isMobile}
                  filterSignupDate={filterSignupDate}
                  filterLastSeenDate={filterLastSeenDate}
                  previewLoading={previewLoading}
                  isSubmitting={isSubmitting}
                  errors={errors}
                  control={control}
                  setFilterSignupDate={setFilterSignupDate}
                  setFilterLastSeenDate={setFilterLastSeenDate}
                  reset={reset}
                  handleClose={handleClose}
                />
              )}
            </form>
          </FormProvider>
        </Card>

        {/* Vertical divider for desktop */}
        {!isMobile && <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />}

        {/* Right Pane (preview) */}
        <Box
          sx={{
            flex: 1,
            p: isMobile ? 0 : 3,
            minWidth: 250,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {previewMode && previewData && (
            <Card
              elevation={4}
              sx={{
                width: '100%',
                maxWidth: 600,
                p: isMobile ? 2 : 4,
                mx: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                boxShadow: (theme) => theme.shadows[1],
                borderRadius: 3,
                background:
                  'linear-gradient(135deg, rgba(245,245,245,1) 0%, rgba(255,255,255,1) 100%)',
              }}
            >
              {/* Preview Title */}
              <Box sx={{ pb: 2, borderBottom: '2px solid', borderColor: 'primary.main' }}>
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  sx={{
                    fontWeight: 'bold',
                    letterSpacing: 1,
                    color: 'primary.main',
                    textAlign: 'center',
                  }}
                >
                  Campaign Preview
                </Typography>
              </Box>

              {/* Notification Details */}
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      fontWeight: 'bold'
                    }}
                  >
                    Topic
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                    {previewData.topic}
                  </Typography>
                </Box>

                {/* Message section in Paper */}
                <Paper
                  elevation={1}
                  sx={{
                    bgcolor: 'background.paper',
                    p: isMobile ? 2 : 3,
                    borderRadius: 2,
                    minHeight: 80,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      fontWeight: 'bold'
                    }}
                  >
                    Message
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      mt: 0.5,
                      fontSize: isMobile ? 15 : 16,
                      lineHeight: 1.6,
                      whiteSpace: 'pre-line',
                      color: 'text.primary',
                    }}
                  >
                    {previewData.message}
                  </Typography>
                </Paper>
              </Stack>

              {/* Target Users */}
              <Box
                sx={{
                  my: 2,
                  px: 2,
                  py: 2,
                  borderRadius: 2,
                  bgcolor: (theme) => theme.palette.grey[100],
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    letterSpacing: 1.2,
                    color: 'primary.main',
                  }}
                >
                  Target Users
                </Typography>
                <Typography
                  variant={isMobile ? 'h2' : 'h1'}
                  color="primary"
                  sx={{ fontWeight: '900', letterSpacing: 3 }}
                  aria-label="Number of affected users"
                >
                  {previewData.affected_users.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This is the number of users who will receive this notification.
                </Typography>
              </Box>

              {/* Alert/warning */}
              <Alert severity="warning" sx={{ fontSize: isMobile ? 12 : 14 }}>
                The number of affected users may change depending on user activities until campaign submission.
              </Alert>

              {/* Action Buttons */}
              <Stack
                direction={isMobile ? 'column' : 'row'}
                spacing={2}
                justifyContent="center"
                sx={{ mt: 2, width: '100%' }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setPreviewMode(false)}
                  sx={{
                    width: isMobile ? '100%' : '40%',
                    fontSize: isMobile ? 15 : 17,
                    py: 1.5,
                    textTransform: 'none',
                  }}
                >
                  Edit
                </Button>
                <LoadingButton
                  variant="contained"
                  onClick={onSubmitCampaign}
                  sx={{
                    width: isMobile ? '100%' : '40%',
                    fontSize: isMobile ? 15 : 17,
                    py: 1.5,
                    textTransform: 'none',
                  }}
                  loading={submitLoading}
                >
                  Submit Campaign
                </LoadingButton>
              </Stack>
            </Card>

          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

const LeftPaneContent = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: isMobile ? 'calc(80vh - 64px - 48px)' : 'auto',
        overflowY: isMobile ? 'auto' : 'visible',
        pr: isMobile ? 1 : 0,
      }}
    >
      {children}
    </Box>
  );
};
